import React from "react";
import { FloatingLabel, Form, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { CustomProjectModalProps } from "../CustomProjectModal.types.tsx";

import { ActionButton } from "../../../components/Buttons.tsx";
import { UnitSystemOptionsType } from "../../../components/Settings/Settings.types.tsx";
import { StyledCreatableSelect } from "../../../components/Inputs.tsx";

interface Props extends CustomProjectModalProps {
  onNext: () => void;
  unitSystem: UnitSystemOptionsType;
}

const CustomProjectStepOne: React.FC<Props> = ({
  formData,
  handleChange,
  handleSelectGeneric,
  systems,
  voltages,
  technology,
  refrigerant,
  heatExchanger,
  efficiency,
  locations,
  validated,
  onNext,
  unitSystem,
}) => {
  const { t } = useTranslation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext();
  };

  const disableNumberScroll = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
  };

  const locationOptions = locations?.map((loc) => ({
    value: loc.name,
    label: loc.name,
  }));

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit}>
      {/* Project Name */}
      <FloatingLabel
        controlId="floatingProjectName"
        label={t("customProjectModal.step1.projectName")}
        className="mb-3"
      >
        <Form.Control
          type="text"
          name="name"
          placeholder=""
          value={formData.name}
          onChange={handleChange}
          required
        />
        <Form.Control.Feedback type="invalid">
          {t("customProjectModal.step1.errors")}
        </Form.Control.Feedback>
      </FloatingLabel>

      {/* Description */}
      <FloatingLabel
        controlId="floatingDescription"
        label={t("customProjectModal.step1.description")}
        className="mb-3"
      >
        <Form.Control
          type="text"
          name="description"
          placeholder=""
          value={formData.description}
          onChange={handleChange}
          required
        />
        <Form.Control.Feedback type="invalid">
          {t("customProjectModal.step1.errors")}
        </Form.Control.Feedback>
      </FloatingLabel>

      {/* System */}
      <FloatingLabel
        controlId="floatingSystem"
        label={t("customProjectModal.step1.system")}
        className="mb-3"
      >
        <Form.Select name="system_id" value={formData.system_id} onChange={handleSelectGeneric}>
          <option value="" disabled>
            {t("customProjectModal.step1.selectOption")}
          </option>
          {systems?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Form.Select>
      </FloatingLabel>

      {/* Voltage */}
      <FloatingLabel
        controlId="floatingVoltages"
        label={t("customProjectModal.step1.voltages")}
        className="mb-3"
      >
        <Form.Select
          name="voltage_id"
          value={formData.voltage_id}
          onChange={handleSelectGeneric}
          disabled={!voltages}
        >
          <option value="" disabled>
            {t("customProjectModal.step1.selectOption")}
          </option>
          {voltages?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Form.Select>
      </FloatingLabel>

      {/* Technology */}
      <FloatingLabel
        controlId="floatingTechnology"
        label={t("customProjectModal.step1.technology")}
        className="mb-3"
      >
        <Form.Select
          name="technology_id"
          value={formData.technology_id}
          onChange={handleSelectGeneric}
          disabled={!technology}
        >
          <option value="" disabled>
            {t("customProjectModal.step1.selectOption")}
          </option>
          {technology?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Form.Select>
      </FloatingLabel>

      {/* Refrigerant */}
      <FloatingLabel
        controlId="floatingRefrigerant"
        label={t("customProjectModal.step1.refrigerant")}
        className="mb-3"
      >
        <Form.Select
          name="refrigerant_id"
          value={formData.refrigerant_id}
          onChange={handleSelectGeneric}
          disabled={!refrigerant}
        >
          <option value="" disabled>
            {t("customProjectModal.step1.selectOption")}
          </option>
          {refrigerant?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Form.Select>
      </FloatingLabel>

      {/* Nominal Capacity */}
      <InputGroup className="mb-3">
        <FloatingLabel
          controlId="floatingCapacities"
          label={t("customProjectModal.step1.capacities")}
        >
          <Form.Control
            type="number"
            name="nominal_capacity"
            placeholder=""
            value={formData.nominal_capacity}
            onChange={handleChange}
            required
            disabled={!formData.refrigerant_id}
            onWheel={disableNumberScroll}
            min={0}
            step="any"
          />
        </FloatingLabel>
        <InputGroup.Text>{t(`units.${unitSystem}.weight`)}</InputGroup.Text>
      </InputGroup>

      {/* Heat Exchanger */}
      <FloatingLabel
        controlId="floatingHeatExchanger"
        label={t("customProjectModal.step1.heatExchanger")}
        className="mb-3"
      >
        <Form.Select
          name="heat_exchanger_id"
          value={formData.heat_exchanger_id}
          onChange={handleSelectGeneric}
          disabled={!heatExchanger}
        >
          <option value="" disabled>
            {t("customProjectModal.step1.selectOption")}
          </option>
          {heatExchanger?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.type || s.name}
            </option>
          ))}
        </Form.Select>
      </FloatingLabel>

      {/* Efficiency */}
      <FloatingLabel
        controlId="floatingEfficiency"
        label={t("customProjectModal.step1.efficiency")}
        className="mb-3"
      >
        <Form.Select
          name="efficiency_id"
          value={formData.efficiency_id}
          onChange={handleSelectGeneric}
          disabled={!efficiency}
        >
          <option value="" disabled>
            {t("customProjectModal.step1.selectOption")}
          </option>
          {efficiency?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Form.Select>
      </FloatingLabel>

      {/* Fluid type */}
      <FloatingLabel
        controlId="floating_fluid_type"
        label={t(`customProjectModal.step1.fluidType`)}
        className="mb-3"
      >
        <Form.Select
          name="custom_system[fluid_type]"
          value={formData.custom_system.fluid_type}
          onChange={handleSelectGeneric}
        >
          <option value="" disabled>
            {t("customProjectModal.step1.selectOption")}
          </option>
          <option value="Water">{t(`customProjectModal.step1.water`)}</option>
          <option value="Propylene glycol">{t(`customProjectModal.step1.propyleneGlycol`)}</option>
        </Form.Select>
      </FloatingLabel>

      {/* Fluid Concentration */}
      {formData.custom_system.fluid_type !== "Water" && (
        <FloatingLabel
          controlId="floating_fluid_concentration"
          label={t(`customProjectModal.step1.fluidConcentration`)}
          className="mb-3"
        >
          <Form.Select
            name="custom_system[fluid_concentration]"
            value={formData.custom_system.fluid_concentration}
            onChange={handleSelectGeneric}
          >
            <option value="" disabled>
              {t("customProjectModal.step1.selectOption")}
            </option>
            <option value={20}>20%</option>
            <option value={30}>30%</option>
            <option value={40}>40%</option>
            <option value={50}>50%</option>
            <option value={60}>60%</option>
          </Form.Select>
        </FloatingLabel>
      )}

      {/* Leaving Water Temperature / Evaporating Temperature */}
      <InputGroup className="mb-3">
        <FloatingLabel
          controlId="floatingLeavingTemp"
          label={
            ["5", "6"].includes(formData?.system_id)
              ? t("customProjectModal.step1.evaporatingTemperature")
              : t("customProjectModal.step1.leavingWaterTemp")
          }
        >
          <Form.Control
            type="number"
            name="leaving_water_temperature"
            placeholder=""
            value={formData.leaving_water_temperature}
            onChange={handleChange}
            step="any"
            required
            onWheel={disableNumberScroll}
          />

          <Form.Control.Feedback type="invalid">
            {t("customProjectModal.step1.errors")}
          </Form.Control.Feedback>
        </FloatingLabel>
        <InputGroup.Text>{t(`units.${unitSystem}.temperature`)}</InputGroup.Text>
      </InputGroup>

      {/* Entering Water Temperature */}
      <InputGroup className="mb-3">
        <FloatingLabel
          controlId="floatingEnteringTemp"
          label={t("customProjectModal.step1.enteringWaterTemp")}
        >
          <Form.Control
            type="number"
            name="entering_water_temperature"
            placeholder=""
            value={formData.entering_water_temperature}
            onChange={handleChange}
            step="any"
            required
            onWheel={disableNumberScroll}
          />

          <Form.Control.Feedback type="invalid">
            {t("customProjectModal.step1.errors")}
          </Form.Control.Feedback>
        </FloatingLabel>
        <InputGroup.Text>{t(`units.${unitSystem}.temperature`)}</InputGroup.Text>
      </InputGroup>

      {/* Location */}
      <FloatingLabel
        controlId="floatingLocation"
        label={t("standardProjectModal.location")}
        className={`mb-3 ${formData.location ? "label-floating-fixed" : ""}`}
      >
        <StyledCreatableSelect
          isClearable
          options={locationOptions}
          classNamePrefix="react-select"
          value={
            locationOptions?.find((opt) => opt.value === formData.location) ||
            (formData.location ? { label: formData.location, value: formData.location } : null)
          }
          onChange={(newValue) => {
            handleSelectGeneric({
              target: {
                name: "location",
                value: newValue ? newValue.value : "",
              },
            });
          }}
          formatCreateLabel={(inputValue) => `Add: "${inputValue}"`}
          placeholder={null}
        />
      </FloatingLabel>

      {/* Ambiance Temperature */}
      <InputGroup className="mb-3">
        <FloatingLabel
          controlId="floatingAmbiance"
          label={t("customProjectModal.step1.ambianceTemperature")}
        >
          <Form.Control
            type="number"
            name="ambiance_temperature"
            placeholder=""
            value={formData.ambiance_temperature}
            onChange={handleChange}
            step="any"
            required
            onWheel={disableNumberScroll}
          />

          <Form.Control.Feedback type="invalid">
            {t("customProjectModal.step1.errors")}
          </Form.Control.Feedback>
        </FloatingLabel>
        <InputGroup.Text>{t(`units.${unitSystem}.temperature`)}</InputGroup.Text>
      </InputGroup>

      {/* AMSL */}
      <InputGroup className="mb-3">
        <FloatingLabel controlId="floatingAMSL" label={t("customProjectModal.step1.amsl")}>
          <Form.Control
            type="number"
            name="amsl"
            placeholder=""
            value={formData.amsl}
            onChange={handleChange}
            required
            onWheel={disableNumberScroll}
          />
        </FloatingLabel>
        <InputGroup.Text>{t(`units.${unitSystem}.length`)}</InputGroup.Text>
      </InputGroup>

      {/* Pressure Drop */}
      <InputGroup className="mb-3">
        <FloatingLabel
          controlId="floatingPressureDrop"
          label={t("customProjectModal.step1.pressureDrop")}
        >
          <Form.Control
            type="number"
            name="pressure_drop"
            placeholder=""
            value={formData.pressure_drop}
            onChange={handleChange}
            step="any"
            required
            onWheel={disableNumberScroll}
          />

          <Form.Control.Feedback type="invalid">
            {t("customProjectModal.step1.errors")}
          </Form.Control.Feedback>
        </FloatingLabel>
        <InputGroup.Text>
          ft.H<sub>2</sub>O
        </InputGroup.Text>
      </InputGroup>
      <div className="d-flex justify-content-between mt-3 buttonsContainer">
        <ActionButton type="submit">{t("customProjectModal.step1.next")}</ActionButton>
      </div>
    </Form>
  );
};

export default CustomProjectStepOne;
